import type { ItemNode, OrdersParseTree } from "../parser/types";
import { extractBlanks } from "./blanks";
import { normalizeSpeechText } from "./speech";
import type {
  BlankField,
  CriticalKind,
  CriticalSpan,
  NormalizedOrders,
  NormalizeOrdersOptions,
  UnresolvedTokenLogEntry,
  UnresolvedTokenOccurrence,
} from "./types";

function replaceBlanks(
  text: string,
  blanks: readonly BlankField[],
  fills: Readonly<Record<string, string>>,
): string {
  let index = 0;
  const replaced = text.replace(/_9C|_+/g, (token) => {
    const blank = blanks[index++];
    /* v8 ignore next -- blanks are extracted from this same text and regex */
    if (blank === undefined) return token;
    const fill = fills[blank.id]?.trim();
    if (token === "_9C")
      return fill === undefined || fill === "" ? SILENT_BLANK : `${fill}9C`;
    return fill === undefined || fill === "" ? SILENT_BLANK : fill;
  });
  const emptyCell = blanks.find(({ sourceToken }) => sourceToken === "");
  if (emptyCell === undefined) return replaced;
  const fill = fills[emptyCell.id]?.trim();
  return `${replaced} | ${fill === undefined || fill === "" ? SILENT_BLANK : fill}`;
}

/**
 * What an unfilled blank contributes to the spoken stream: nothing.
 *
 * Ruling 69a of 14 October 2026 amends Ruling 63. It used to speak the word
 * "blank", which doc 04 section 6.4 allows and Ruling 63 instructed — and
 * which, read aloud fifty-two times through a warning order and an orders
 * card, told the trainee nothing and wasted his run. Ruling 69b stops an
 * incomplete document starting at all, so nothing should reach this; it is
 * silent rather than removed so that a blank which somehow does reach
 * delivery is heard as a gap and never as a word.
 */
const SILENT_BLANK = "";

function itemKind(
  item: ItemNode,
  paragraphId: string,
): readonly CriticalKind[] {
  if (paragraphId === "MISSION") return ["mission"];
  if (
    item.type === "table" &&
    !/^Timing\s*\|\s*Value$/i.test(item.displayText)
  ) {
    return ["timing"];
  }
  return [];
}

export function normalizeOrders(
  tree: OrdersParseTree,
  options: NormalizeOrdersOptions = {},
): NormalizedOrders {
  const blanks = extractBlanks(tree);
  const blanksByNode = new Map<string, BlankField[]>();
  for (const blank of blanks) {
    const existing = blanksByNode.get(blank.nodeId) ?? [];
    existing.push(blank);
    blanksByNode.set(blank.nodeId, existing);
  }
  const fills = options.fills ?? {};
  const criticalSpans: CriticalSpan[] = [];
  const unresolvedTokens: UnresolvedTokenOccurrence[] = [];
  const logEntries: UnresolvedTokenLogEntry[] = [];
  const spokenTextStream: string[] = [];
  const repeatedNodeIds: string[] = [];

  const append = (
    displayText: string,
    nodeId: string,
    forcedKinds: readonly CriticalKind[] = [],
  ): void => {
    const source = replaceBlanks(
      displayText,
      blanksByNode.get(nodeId) ?? [],
      fills,
    );
    const normalized = normalizeSpeechText(source, { nodeId });
    spokenTextStream.push(normalized.spokenText);
    if (normalized.saysTwice) repeatedNodeIds.push(nodeId);
    unresolvedTokens.push(...normalized.unresolvedTokens);
    logEntries.push(...normalized.logEntries);
    for (const kind of new Set([...forcedKinds, ...normalized.criticalKinds])) {
      criticalSpans.push({ id: `${nodeId}/critical-${kind}`, nodeId, kind });
    }
  };

  for (const paragraph of tree.paragraphs) {
    append(paragraph.spokenHeading, paragraph.id);
    for (const item of paragraph.items) {
      append(item.spokenText, item.id, itemKind(item, paragraph.id));
    }
    for (const subparagraph of paragraph.subparagraphs) {
      append(subparagraph.spokenHeading, subparagraph.id);
      for (const item of subparagraph.items) {
        append(item.spokenText, item.id, itemKind(item, paragraph.id));
      }
    }
  }

  return {
    schemaVersion: 1,
    displayTree: tree,
    blanks,
    repeatedNodeIds,
    spokenTextStream,
    unresolvedTokens,
    logEntries,
    criticalSpans,
  };
}
